/**
 * Sanitize Allowlist —— 三方共享 XSS 净化白名单的 typed loader。
 *
 * 单一来源：仓库根 `sanitize.allowlist.json`（schema 见 `../../sanitize.allowlist.schema.json`）。
 * 消费侧：
 *   - 前台 DOMPurify（F6 接入）
 *   - 管理端 wangEditor（A 系列接入）
 *   - 后端 HtmlSanitizer（B3 通过 IConfiguration 读取）
 *
 * 本模块不依赖文件系统；调用方负责把 JSON 解析后的对象传给 `parseAllowlist`，
 * 返回一个深度冻结的 typed 副本。运行时校验失败即抛错，遵循 Debug-First。
 */
export { loadAllowlist, toDomPurifyConfig, } from './dompurify.js';
const SUPPORTED_VERSIONS = [1];
/**
 * Root 级允许的字段。`$schema` 是 JSON Schema 的 metadata key（编辑器 / 工具链
 * 用于关联 schema），不参与运行时语义但保留入参合法。其余 key 进 typed 解析。
 */
const ALLOWED_ROOT_KEYS = new Set([
    '$schema',
    'version',
    'allowedTags',
    'allowedAttributes',
    'allowedSchemes',
    'allowedCssProperties',
]);
export class AllowlistConfigError extends Error {
    constructor(message) {
        super(`[sanitize.allowlist] ${message}`);
        this.name = 'AllowlistConfigError';
    }
}
/**
 * 解析任意来源的 raw 配置（通常是 `JSON.parse(fileContent)` 的结果），返回深冻结的
 * typed `AllowlistConfig`。schema 偏离即抛 `AllowlistConfigError`。
 *
 * 不接受未知字段：root 级 typo（如 `allowedTages`）会显式抛错而非静默丢弃，
 * 避免净化白名单"看似配置生效实则被忽略"的 XSS 风险。
 */
export function parseAllowlist(raw) {
    const obj = expectObject(raw, 'root');
    rejectUnknownRootKeys(obj);
    const version = expectNumber(obj.version, 'version');
    if (!SUPPORTED_VERSIONS.includes(version)) {
        throw new AllowlistConfigError(`unsupported version=${version}; supported=${SUPPORTED_VERSIONS.join(',')}`);
    }
    const allowedTags = expectStringArray(obj.allowedTags, 'allowedTags');
    const allowedSchemes = expectStringArray(obj.allowedSchemes, 'allowedSchemes');
    const allowedAttributes = parseAttributeMap(obj.allowedAttributes);
    const allowedCssProperties = expectStringArray(obj.allowedCssProperties, 'allowedCssProperties');
    return Object.freeze({
        version,
        allowedTags: Object.freeze([...allowedTags]),
        allowedAttributes,
        allowedSchemes: Object.freeze([...allowedSchemes]),
        allowedCssProperties: Object.freeze([...allowedCssProperties]),
    });
}
function rejectUnknownRootKeys(obj) {
    const unknown = Object.keys(obj).filter((k) => !ALLOWED_ROOT_KEYS.has(k));
    if (unknown.length > 0) {
        throw new AllowlistConfigError(`unknown root keys: ${unknown.join(', ')}; allowed: ${[
            ...ALLOWED_ROOT_KEYS,
        ].join(', ')}`);
    }
}
function parseAttributeMap(raw) {
    const obj = expectObject(raw, 'allowedAttributes');
    const out = {};
    for (const [tag, attrs] of Object.entries(obj)) {
        out[tag] = Object.freeze([
            ...expectStringArray(attrs, `allowedAttributes.${tag}`),
        ]);
    }
    return Object.freeze(out);
}
function expectObject(value, path) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        throw new AllowlistConfigError(`${path} must be a plain object`);
    }
    return value;
}
function expectNumber(value, path) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new AllowlistConfigError(`${path} must be a finite number`);
    }
    return value;
}
function expectStringArray(value, path) {
    if (!Array.isArray(value)) {
        throw new AllowlistConfigError(`${path} must be an array of strings`);
    }
    return value.map((item, index) => {
        if (typeof item !== 'string' || item.length === 0) {
            throw new AllowlistConfigError(`${path}[${index}] must be a non-empty string`);
        }
        return item;
    });
}
//# sourceMappingURL=index.js.map